// Quote Preview — supports both PDF (via xhtml2pdf bytes) and HTML modes.
// HTML mode is A4-shaped so users can Save-as-PDF from the browser print dialog,
// giving us a reliable fallback while the PDF renderer is being polished / redesigned.
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, RefreshCw, FileText, Code2, Printer } from "lucide-react";
import { GBP, quoteFileTitle } from "./_quoteHelpers";
import { QUOTE_VARIANTS, DEFAULT_VARIANT, isProforma } from "./quoteVariants";
import SendQuoteDialog from "./panels/SendQuoteDialog";
import { useProformaIssue, proformaRef } from "./panels/ProformaIssue";
import { BadgeCheck } from "lucide-react";

export default function QuotePdfPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [htmlLoading, setHtmlLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [mode, setMode] = useState("html"); // default to HTML while PDF renderer is being polished
  const [pdfEngine, setPdfEngine] = useState(null);
  const [bust, setBust] = useState(0);
  const [variants, setVariants] = useState([]);
  const [variant, setVariant] = useState("");
  const [tpl, setTpl] = useState("");
  const [variantsLoaded, setVariantsLoaded] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const htmlIframeRef = useRef(null);

  // Resolve the default variant FIRST, then let the data effects run once —
  // avoids the double-fire ('' -> 'long') that used to trigger duplicate calls.
  useEffect(() => {
    api.get(`/quote-pdf/content`).then(({ data }) => {
      const items = data?.items || [];
      setVariants(items);
      setVariant((items.find((v) => v.is_default) || items[0])?._id || "");
    }).catch(() => {}).finally(() => setVariantsLoaded(true));
  }, []);

  // Fast path: quote + HTML preview. Renders immediately; NOT gated behind PDF.
  useEffect(() => {
    if (!variantsLoaded) return;
    let alive = true;
    (async () => {
      setHtmlLoading(true);
      try {
        const vq = variant ? `&variant=${variant}` : "";
        const [{ data: q }, htmlRes] = await Promise.all([
          api.get(`/quotes/${id}`),
          api.get(`/quotes/${id}/pdf-preview?t=${bust}${vq}`, { responseType: "text", transformResponse: [(v) => v] }),
        ]);
        if (!alive) return;
        setQuote(q);
        setTpl((t) => t || q.QuoteVariant || DEFAULT_VARIANT);
        setHtmlContent(typeof htmlRes.data === "string" ? htmlRes.data : String(htmlRes.data || ""));
      } catch (e) {
        console.error(e);
        toast.error("Failed to load quote preview");
      } finally { if (alive) setHtmlLoading(false); }
    })();
    return () => { alive = false; };
  }, [id, bust, variant, variantsLoaded]);

  // Lazy path: heavy WeasyPrint PDF fetched only when the user is in PDF mode.
  useEffect(() => {
    if (mode !== "pdf" || !variantsLoaded) return;
    let alive = true;
    (async () => {
      setPdfLoading(true);
      try {
        const vq = variant ? `&variant=${variant}` : "";
        const pdfRes = await api.get(`/quotes/${id}/pdf?mode=inline&t=${bust}${vq}`, { responseType: "blob" });
        if (!alive) return;
        setPdfEngine(pdfRes.headers?.["x-pdf-engine"] || "chrome");
        const url = URL.createObjectURL(new File([pdfRes.data], `${fileTitle}.pdf`, { type: "application/pdf" }));
        setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch (e) {
        console.error(e);
        toast.error("PDF failed to render — use HTML mode / Print as fallback");
      } finally { if (alive) setPdfLoading(false); }
    })();
    return () => { alive = false; };
  }, [id, bust, variant, mode, variantsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const cust = quote?.Customer || {};
  const totals = quote?.Totals || {};
  const custName = useMemo(() => (
    [cust.firstName, cust.lastName].filter(Boolean).join(" ") || cust.email || "—"
  ), [cust]);
  const fileTitle = useMemo(() => quoteFileTitle(quote), [quote]);
  const pfMode = isProforma(tpl);
  const pf = useProformaIssue(quote, async () => { try { const { data } = await api.get(`/quotes/${id}`); setQuote(data); } catch { /* refresh best-effort */ } });
  const issued = (how) => { if (pfMode) pf.trigger(how); };

  // Template look (light-mono / light-navy / proposal / email) drives the V2
  // render via quote.QuoteVariant. Persist the pick so it matches the builder.
  const changeTemplate = async (key) => {
    setTpl(key);
    try { await api.patch(`/quotes/${id}`, { QuoteVariant: key }); setBust((n) => n + 1); }
    catch { toast.error("Couldn't switch template"); }
  };

  const downloadPdf = async () => {    try {
      const res = await api.get(`/quotes/${id}/pdf?mode=download${variant ? `&variant=${variant}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${fileTitle}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      issued("download_pdf");
    } catch { toast.error("Download failed"); }
  };

  const downloadHtml = () => {
    try {
      if (!htmlContent) return toast.error("HTML not ready");
      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${fileTitle}.html`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      issued("download_html");
    } catch { toast.error("HTML download failed"); }
  };

  const printHtml = () => {
    const iframe = htmlIframeRef.current;
    if (!iframe || !iframe.contentWindow) return toast.error("Preview not ready");
    try {
      // Browsers use the document title as the default "Save as PDF" filename.
      const prevTitle = document.title;
      document.title = fileTitle;
      if (iframe.contentDocument) iframe.contentDocument.title = fileTitle;
      const restore = () => { document.title = prevTitle; };
      iframe.contentWindow.addEventListener("afterprint", restore, { once: true });
      setTimeout(restore, 60000);
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      issued("printed");
    } catch (e) {
      console.error(e);
      toast.error("Print dialog blocked");
    }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="quote-pdf-preview-page">
        {/* Header strip */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-orange-400/80 font-mono">
              <FileText className="w-3.5 h-3.5" /> {pfMode ? "Pro Forma Invoice Preview" : "Quote Preview"}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mt-1">
              {pfMode && quote ? proformaRef(quote.QuoteRef) : (quote?.QuoteRef || "…")} <span className="text-slate-500 font-normal text-base">· {custName}</span>
            </h1>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              Total {GBP(totals.total)} · {pfMode ? `Pro forma against quote ${quote?.QuoteRef || ""} · printing, downloading or sending asks to move it to Accepted (not paid).` : "This is the exact document that will be sent to the customer."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Template look — drives the V2 render (persists on the quote) */}
            <select
              value={tpl}
              onChange={(e) => changeTemplate(e.target.value)}
              className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-200 focus:outline-none focus:border-orange-400"
              data-testid="preview-variant-select"
              title="Choose which quote template to render"
            >
              {QUOTE_VARIANTS.map((v) => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </select>
            {/* View mode toggle */}
            <div className="inline-flex rounded-lg border border-white/10 overflow-hidden" role="tablist" data-testid="preview-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("html")}
                className={`text-xs font-mono uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5 ${mode === "html" ? "bg-orange-500 text-black" : "text-slate-300 hover:bg-white/5"}`}
                data-testid="preview-mode-html"
                aria-pressed={mode === "html"}
              >
                <Code2 className="w-3.5 h-3.5" /> HTML
              </button>
              <button
                type="button"
                onClick={() => setMode("pdf")}
                className={`text-xs font-mono uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5 border-l border-white/10 ${mode === "pdf" ? "bg-orange-500 text-black" : "text-slate-300 hover:bg-white/5"}`}
                data-testid="preview-mode-pdf"
                aria-pressed={mode === "pdf"}
              >
                <FileText className="w-3.5 h-3.5" /> PDF
              </button>
            </div>

            <button
              onClick={() => navigate(`/quotes/${id}/edit`)}
              className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 inline-flex items-center gap-1.5"
              data-testid="pdf-back-to-builder"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Builder
            </button>
            <button
              onClick={() => setBust((n) => n + 1)}
              className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 inline-flex items-center gap-1.5"
              data-testid="pdf-refresh"
              title="Rebuild from latest quote data"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={() => setEmailOpen(true)}
              title="Demo — prepares the email and new-design PDF attachment; nothing is sent to the customer."
              className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-teal-500/40 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20 disabled:opacity-45 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="btn-send-email"
            >
              <Send className="w-3.5 h-3.5" /> Send to customer · demo
            </button>
            {pfMode && (
              <button onClick={() => pf.trigger("manual")} data-testid="proforma-mark-issued"
                title="Record that this pro forma was printed or sent outside the app"
                className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 inline-flex items-center gap-1.5">
                <BadgeCheck className="w-3.5 h-3.5" /> Mark pro forma issued
              </button>
            )}

            {mode === "html" ? (
              <>
                <button
                  onClick={printHtml}
                  className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5"
                  data-testid="html-print"
                  title="Open browser print dialog — choose 'Save as PDF' as destination"
                >
                  <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                </button>
                <button
                  onClick={downloadHtml}
                  className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg bg-orange-500 text-black font-bold hover:bg-orange-400 inline-flex items-center gap-1.5"
                  data-testid="html-download"
                >
                  <Download className="w-3.5 h-3.5" /> Download HTML
                </button>
              </>
            ) : (
              <button
                onClick={downloadPdf}
                className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg bg-orange-500 text-black font-bold hover:bg-orange-400 inline-flex items-center gap-1.5"
                data-testid="pdf-download"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
            )}
          </div>
        </div>

        {/* Viewer */}
        <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden" style={{ height: "82vh" }}>
          {mode === "pdf" && pdfLoading && (
            <div className="w-full h-full grid place-items-center text-slate-500 text-sm">Rendering PDF…</div>
          )}
          {mode === "pdf" && !pdfLoading && blobUrl && pdfEngine === "weasyprint" && (
            <div className="px-4 py-2 text-[11.5px] font-mono text-amber-200 bg-amber-500/10 border-b border-amber-500/30" data-testid="pdf-engine-note">
              Rendered with the backup PDF engine — layout may differ slightly. For an exact copy use HTML → Print / Save as PDF.
            </div>
          )}
          {mode === "pdf" && !pdfLoading && blobUrl && (
            <iframe
              title={`PDF preview for ${quote?.QuoteRef}`}
              src={blobUrl}
              className="w-full h-full border-0"
              data-testid="pdf-iframe"
            />
          )}
          {mode === "pdf" && !pdfLoading && !blobUrl && (
            <div className="w-full h-full grid place-items-center text-red-400 text-sm">PDF failed to load. Switch to HTML mode as a fallback.</div>
          )}

          {mode === "html" && htmlLoading && (
            <div className="w-full h-full grid place-items-center text-slate-500 text-sm">Rendering preview…</div>
          )}
          {mode === "html" && !htmlLoading && (
            <div className="w-full h-full overflow-auto py-6 flex justify-center bg-neutral-800">
              {/* A4 sheet: 210 × 297 mm at 96dpi ≈ 794 × 1123 px per page */}
              <div
                className="bg-white shadow-2xl"
                style={{ width: "794px" }}
                data-testid="html-a4-sheet"
              >
                <iframe
                  ref={htmlIframeRef}
                  title={`HTML preview for ${quote?.QuoteRef}`}
                  srcDoc={htmlContent}
                  className="w-full border-0 block"
                  style={{ width: "794px", height: "2260px" }}
                  onLoad={(e) => {
                    try {
                      const doc = e.currentTarget.contentDocument;
                      if (doc && doc.body) {
                        const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
                        e.currentTarget.style.height = `${h + 20}px`;
                      }
                    } catch { /* cross-origin safety */ }
                  }}
                  data-testid="html-iframe"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <SendQuoteDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        quote={quote}
        onSent={async () => {
          issued("sent");
          try {
            const { data } = await api.get(`/quotes/${id}`);
            setQuote(data);
            setBust((n) => n + 1);
          } catch (err) {
            if (process.env.NODE_ENV !== "production") console.warn("Post-send quote refresh failed:", err);
          }
        }}
      />
      {pf.dialog}
    </Layout>
  );
}
